/**
 * Browser-safe ID and random helpers (S2245). Use instead of Math.random for IDs/delays.
 */

export { makePrefixedId, STEEL_ACCOUNTING_ID_PREFIXES } from '@repo/shared-utils';

/** Inclusive integer in [min, max] for simulation delays etc. */
export function secureRandomIntInclusive(min: number, max: number): number {
  if (max < min) throw new Error('secureRandomIntInclusive: max < min');
  const range = max - min + 1;
  const buf = new Uint32Array(1);
  globalThis.crypto.getRandomValues(buf);
  return min + (buf[0] % range);
}

const ALPHANUM_LOWER = '0123456789abcdefghijklmnopqrstuvwxyz';

/** Fixed-length lowercase alphanumeric string (client-only IDs). */
export function makeShortAlphanumeric(length: number): string {
  const bytes = new Uint8Array(length);
  globalThis.crypto.getRandomValues(bytes);
  let out = '';
  for (let i = 0; i < length; i++) {
    out += ALPHANUM_LOWER[bytes[i] % ALPHANUM_LOWER.length];
  }
  return out;
}

/**
 * RFC 4122 UUID for optimistic / client-side entity ids. Prefer this over `t${Date.now()}`, `L-…`,
 * or other timestamp prefixes so values stay compatible with API `zTransitionIdCreateOptional`.
 */
export function newClientEntityId(): string {
  const c = globalThis.crypto;
  if (c?.randomUUID) {
    return c.randomUUID();
  }
  if (!c?.getRandomValues) {
    throw new Error('Web Crypto is required for newClientEntityId');
  }
  const buf = new Uint8Array(16);
  c.getRandomValues(buf);
  buf[6] = (buf[6] & 0x0f) | 0x40;
  buf[8] = (buf[8] & 0x3f) | 0x80;
  const hex = [...buf].map((b) => b.toString(16).padStart(2, '0')).join('');
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
}
